import React, { useState } from "react";
import { GasPump, ArrowClockwise, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/defaults";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

const guessState = (city) => {
  if (!city) return "";
  const m = city.match(/,\s*([A-Z]{2})\s*$/);
  return m ? m[1] : "";
};

export const FuelAutoFill = ({ pickCity, deliveryCity, onApply }) => {
  const [open, setOpen] = useState(false);
  const [origin, setOrigin] = useState(guessState(pickCity) || "KY");
  const [dest, setDest] = useState(guessState(deliveryCity) || "NC");
  const [mpg, setMpg] = useState(6.5);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    const o = guessState(pickCity);
    const d = guessState(deliveryCity);
    if (o) setOrigin(o);
    if (d) setDest(d);
  }, [pickCity, deliveryCity]);

  const fetchFuel = async () => {
    setLoading(true);
    try {
      const res = await api.get("/fuel/prices", {
        params: {
          origin_state: origin,
          dest_state: dest,
          mpg,
        },
      });
      setResult(res.data);
    } catch (e) {
      toast.error("Failed to fetch fuel prices");
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    if (!result) return;
    onApply(result.cost_per_mile);
    setOpen(false);
    toast.success(`Fuel CPM set to $${result.cost_per_mile.toFixed(2)}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid="btn-fuel-autofill"
          type="button"
          variant="outline"
          size="sm"
          className="h-9 rounded-lg border-slate-300 text-slate-700 text-xs gap-1.5"
        >
          <GasPump size={14} weight="duotone" className="text-orange-600" />
          Auto CPM
          <Sparkle size={10} weight="fill" className="text-emerald-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4 bg-white" align="end">
        <div>
          <p className="text-sm font-semibold text-slate-900">
            Live diesel from EIA
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Fetches current retail diesel price by PADD region.
          </p>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.1em] text-slate-500">
              Origin state
            </Label>
            <select
              data-testid="fuel-origin-state"
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              className="mt-1 w-full h-9 rounded-lg border border-slate-300 bg-white text-sm px-2"
            >
              {US_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.1em] text-slate-500">
              Destination
            </Label>
            <select
              data-testid="fuel-dest-state"
              value={dest}
              onChange={(e) => setDest(e.target.value)}
              className="mt-1 w-full h-9 rounded-lg border border-slate-300 bg-white text-sm px-2"
            >
              {US_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-[0.1em] text-slate-500">
              Truck MPG
            </Label>
            <Input
              data-testid="fuel-mpg"
              type="number"
              step="0.1"
              value={mpg}
              onChange={(e) => setMpg(parseFloat(e.target.value) || 6.5)}
              className="mt-1 h-9 rounded-lg border-slate-300 tabular"
            />
          </div>
        </div>

        <Button
          data-testid="btn-fetch-fuel"
          onClick={fetchFuel}
          disabled={loading}
          size="sm"
          className="mt-4 w-full h-9 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-xs"
        >
          {loading ? (
            <>
              <ArrowClockwise size={14} weight="bold" className="animate-spin me-1.5" />
              Fetching...
            </>
          ) : (
            <>
              <ArrowClockwise size={14} weight="bold" className="me-1.5" />
              Fetch latest price
            </>
          )}
        </Button>

        {result && (
          <div
            data-testid="fuel-result"
            className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3"
          >
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{result.origin?.state} diesel</span>
              <span className="tabular font-semibold text-slate-900">
                {fmtMoney(result.origin?.price)}/gal
              </span>
            </div>
            {result.dest && (
              <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
                <span>{result.dest?.state} diesel</span>
                <span className="tabular font-semibold text-slate-900">
                  {fmtMoney(result.dest?.price)}/gal
                </span>
              </div>
            )}
            <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between">
              <span className="text-xs text-slate-600">Fuel CPM</span>
              <span
                data-testid="fuel-cpm-result"
                className="tabular text-lg font-semibold text-emerald-700"
              >
                ${result.cost_per_mile.toFixed(3)}
              </span>
            </div>
            <p className="mt-1 text-[10px] text-slate-400">
              Source: {result.origin?.source || "eia"} · cached 30 min
            </p>
            <Button
              data-testid="btn-apply-fuel"
              onClick={apply}
              size="sm"
              className="mt-3 w-full h-8 rounded-lg btn-neon text-xs"
            >
              Apply to Cost Per Mile
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
